import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import { MappedSite } from "./sitesPageStore/sitesPageStore";

/**
 * Confirmation step for removing an Immix → Actuate Remote Link mapping.
 *
 * Unlinking only clears the Actuate group on the Remote Link record. Immix
 * keeps sending arm/disarm signals for the site; they are acknowledged as
 * "unlinked" and ignored until someone maps the site again. Schedules that
 * were disabled when Remote Link was turned on are NOT re-enabled here: the
 * site keeps whatever armed state the last Immix signal left it in until a
 * schedule is saved or it is armed/disarmed manually, so the operator has to
 * set the schedule up deliberately.
 */
export const UnlinkRemoteLinkSiteDialog = ({
  site,
  busy,
  onCancel,
  onConfirm,
}: {
  site: MappedSite | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  return (
    <Dialog
      open={site !== null}
      onClose={busy ? undefined : onCancel}
      aria-labelledby="unlink-remote-link-title"
    >
      <DialogTitle
        id="unlink-remote-link-title"
        sx={{ fontFamily: "Mulish", color: "#001943", fontWeight: "bold" }}
      >
        Unlink Immix site?
      </DialogTitle>
      <DialogContent>
        {site && (
          <div className="font-mulish text-actuate-blue">
            <p className="mb-[12px]">
              Immix site{" "}
              <span className="font-bold">{site.immix_group_name}</span> will no
              longer arm or disarm Actuate site{" "}
              <span className="font-bold">{site.admin_group_name}</span>.
            </p>
            <ul className="list-disc pl-[20px] space-y-[6px] text-[14px]">
              <li>
                Arm/disarm signals Immix keeps sending for this site will be
                ignored. The site will show under Unmapped Sites so it can be
                linked again later.
              </li>
              <li>
                Schedules were disabled when Remote Link was enabled and are not
                turned back on automatically. The site stays in its current
                armed state until you set up a schedule on its Schedules tab or
                arm/disarm it manually.
              </li>
              <li>
                Remove the Actuate external system link in Immix as well if you
                want Immix to stop sending signals for this site.
              </li>
            </ul>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <OrangeTextButton text="Cancel" onClick={onCancel} disabled={busy} />
        <OrangeTextButton
          text={busy ? "Unlinking…" : "Unlink"}
          onClick={onConfirm}
          disabled={busy}
        />
      </DialogActions>
    </Dialog>
  );
};
